// Dialog "Mời ê-kíp ly cà phê" (spec 2026-08-20 §5.3): tiền vào tài khoản
// CÔNG TY rồi trả vào lương nhân viên — không phải CK cá nhân. Khách chọn
// gửi cả ê-kíp (chia đều) hoặc 1 người, chọn mức 20k/50k/100k hoặc tuỳ ý,
// quét QR VietQR với nội dung CK dựng sẵn "GNTIP <mã đơn> <mã NV|ALL>".
import { useEffect, useMemo, useState } from "react";
import { Check, Coffee, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/ui/avatar";
import { Button } from "@/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/ui/dialog";
import { formatVND } from "@/lib/format";
import { fetchTipInfo, type TipInfo } from "./review-api";

const TEAM_KEY = "__team__";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return (parts[parts.length - 1] ?? "").slice(0, 2).toUpperCase() || "GN";
}

function vietQRUrl(info: TipInfo, amount: number, addInfo: string): string {
  const bank = info.bank;
  if (!bank) return "";
  const params = new URLSearchParams({
    amount: String(amount),
    addInfo,
    accountName: bank.accountHolder,
  });
  return `https://img.vietqr.io/image/${bank.bankBin}-${bank.accountNumber}-compact2.png?${params.toString()}`;
}

async function copyText(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`Đã sao chép ${label}`);
  } catch {
    toast.error("Không sao chép được — bạn chép tay giúp studio nhé");
  }
}

export function TipDialog({
  orderId,
  open,
  onOpenChange,
}: {
  orderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coffee className="h-5 w-5" /> Mời ê-kíp ly cà phê
          </DialogTitle>
          <DialogDescription>
            Tiền tặng chuyển vào tài khoản công ty và được trả nguyên vẹn cho ê-kíp trong kỳ lương — có sổ sách rõ ràng.
          </DialogDescription>
        </DialogHeader>
        <TipDialogBody key={orderId} orderId={orderId} onOpenChange={onOpenChange} />
      </DialogContent>
    </Dialog>
  );
}

// Thân nằm BÊN TRONG DialogContent nên Radix tự tháo khi hộp đóng: mỗi lần mở
// là một lượt mới, state khai báo sẵn đúng giá trị đầu — khỏi phải reset bằng
// effect (reset đồng bộ trong effect đẻ ra một lượt vẽ thừa). key theo mã đơn
// để đổi đơn giữa chừng cũng làm mới đúng như vậy.
function TipDialogBody({
  orderId,
  onOpenChange,
}: {
  orderId: string;
  onOpenChange: (open: boolean) => void;
}) {
  const [info, setInfo] = useState<TipInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [recipient, setRecipient] = useState(TEAM_KEY);
  const [amount, setAmount] = useState(0);
  const [customAmount, setCustomAmount] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    fetchTipInfo(orderId, { signal: controller.signal })
      .then(setInfo)
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Không tải được thông tin gửi tặng");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [orderId]);

  const suggested = info?.suggestedAmounts ?? [];
  // Đơn chưa phân công ai: BE vẫn mở màn tặng, chỉ trả danh sách người rỗng.
  const chuaPhanEkip = (info?.recipients ?? []).length === 0;

  const addInfo = useMemo(() => {
    if (!info) return "";
    if (recipient === TEAM_KEY) return info.team?.addInfo ?? "";
    return info.recipients?.find((r) => r.staffId === recipient)?.addInfo ?? "";
  }, [info, recipient]);

  const effectiveAmount = useMemo(() => {
    if (amount > 0) return amount;
    const parsed = Number(customAmount.replace(/[^\d]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }, [amount, customAmount]);

  const qrUrl = useMemo(
    () => (info && addInfo && effectiveAmount >= 1000 ? vietQRUrl(info, effectiveAmount, addInfo) : ""),
    [info, addInfo, effectiveAmount],
  );

  return (
    <>
      {loading && (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Đang tải…
        </div>
      )}
      {!loading && error && <p className="py-4 text-sm text-danger-text">{error}</p>}
      {!loading && !error && info && !info.enabled && (
        <p className="py-4 text-sm text-muted-foreground">Tính năng gửi tặng đang tạm tắt — cảm ơn tấm lòng của bạn!</p>
      )}

      {!loading && !error && info?.enabled && (
        <div className="grid gap-4">
          {chuaPhanEkip ? (
            // Khách bo trước khi studio phân công (CEO chốt 23/08/2026):
            // không có ai để chọn, nói thẳng tiền sẽ chia sau cho ê-kíp.
            <p className="rounded-2xl bg-surface-2/50 p-3 text-sm text-muted-foreground">
              Buổi chụp chưa phân công ê-kíp. Bạn vẫn gửi tặng được — studio sẽ chia đều khoản này cho ê-kíp phục vụ
              bạn ngay khi phân công xong.
            </p>
          ) : (
          <div>
            <div className="text-sm font-semibold">Gửi tặng ai?</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setRecipient(TEAM_KEY)}
                className={
                  "flex min-h-11 items-center gap-2 rounded-xl border px-3 py-1.5 text-sm transition-colors " +
                  (recipient === TEAM_KEY
                    ? "border-[var(--color-brand)] bg-[var(--color-brand)]/10 font-semibold"
                    : "bg-surface")
                }
              >
                ☕ Cả ê-kíp{info.team ? ` (chia đều ${info.team.count} người)` : ""}
              </button>
              {(info.recipients ?? []).map((p) => {
                const active = recipient === p.staffId;
                return (
                  <button
                    key={p.staffId}
                    type="button"
                    onClick={() => setRecipient(p.staffId)}
                    className={
                      "flex min-h-11 items-center gap-2 rounded-xl border px-3 py-1.5 text-sm transition-colors " +
                      (active ? "border-[var(--color-brand)] bg-[var(--color-brand)]/10 font-semibold" : "bg-surface")
                    }
                  >
                    <Avatar className="h-7 w-7">
                      {p.avatarUrl ? <AvatarImage src={p.avatarUrl} alt={p.name} /> : null}
                      <AvatarFallback className="text-xs">{initials(p.name)}</AvatarFallback>
                    </Avatar>
                    <span className="max-w-28 truncate">{p.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
          )}

          <div>
            <div className="text-sm font-semibold">Mức gửi tặng</div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {suggested.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => {
                    setAmount(v);
                    setCustomAmount("");
                  }}
                  className={
                    "min-h-11 rounded-xl border text-sm font-semibold transition-colors " +
                    (amount === v ? "border-[var(--color-brand)] bg-[var(--color-brand)]/10" : "bg-surface")
                  }
                >
                  {formatVND(v)}
                </button>
              ))}
            </div>
            <input
              inputMode="numeric"
              value={customAmount}
              onChange={(e) => {
                setCustomAmount(e.target.value);
                setAmount(0);
              }}
              placeholder="Hoặc nhập số khác (đồng)"
              className="mt-2 h-11 w-full rounded-xl border bg-surface px-3 text-sm"
            />
          </div>

          {qrUrl && (
            <div className="grid gap-2 rounded-2xl border bg-surface-2/50 p-3">
              <img src={qrUrl} alt="Mã QR chuyển khoản" className="mx-auto w-full max-w-64 rounded-xl" />
              <div className="text-center text-xs text-muted-foreground">
                Quét bằng app ngân hàng — số tiền và nội dung đã điền sẵn.
              </div>
              <div className="flex items-center justify-between gap-2 rounded-xl bg-surface px-3 py-2 text-sm">
                <span className="truncate font-mono">{addInfo}</span>
                <button
                  type="button"
                  onClick={() => copyText(addInfo, "nội dung chuyển khoản")}
                  className="flex min-h-9 shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground"
                >
                  <Copy className="h-3.5 w-3.5" /> Chép
                </button>
              </div>
              <p className="text-center text-xs text-muted-foreground">
                ⚠️ Chuyển tay thì giữ đúng nội dung trên để tiền tới đúng người.
              </p>
              <div className="flex items-center justify-center gap-1 text-xs text-success-text">
                <Check className="h-3.5 w-3.5" /> {formatVND(effectiveAmount)} ·{" "}
                {recipient !== TEAM_KEY ? "1 người" : chuaPhanEkip ? "ê-kíp phục vụ bạn" : "cả ê-kíp"}
              </div>
            </div>
          )}
          {!qrUrl && effectiveAmount > 0 && effectiveAmount < 1000 && (
            <p className="text-xs text-muted-foreground">Mức gửi tặng tối thiểu 1.000đ.</p>
          )}

          <Button variant="outline" className="min-h-11 w-full" onClick={() => onOpenChange(false)}>
            Đóng
          </Button>
        </div>
      )}
    </>
  );
}
